<template>
  <div>
    <label v-if="withLabel" class="h6 text-muted">{{
      $t("common.company_search.label")
    }}</label>
    <p v-if="withDescription" class="text-muted small">
      {{ $t("common.company_search.description") }}
    </p>

    <SearchableSelectComponent
      :options="combinedOptions"
      :modelValue="selectedValue"
      :placeholder="$t('common.company_search.placeholder')"
      :isLoading="isLoadingCompanies"
      :isError="hasCompanyError"
      :invalid="invalid"
      :disabled="disabled"
      @update:modelValue="handleSelectCompanyById"
      @queryUpdated="handleQueryUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalCompanySearch } from "@/composables/useGlobalCompanySearch";
import type { PickedCompany } from "@/types";
import { filter, join } from "lodash-es";
import { computed } from "vue";

const props = defineProps<{
  countryCode?: string;
  withLabel?: boolean;
  withDescription?: boolean;
  modelValue?: string;
  invalid?: boolean;
  disabled?: boolean;
}>();

const withLabel = computed(() =>
  props.withLabel === undefined ? true : props.withLabel
);
const withDescription = computed(() =>
  props.withDescription === undefined ? true : props.withDescription
);

const emit = defineEmits<{
  select: [value: PickedCompany];
  "update:modelValue": [value: string];
  input: [value: string];
}>();

// Use the company search composable with the provided country code
const {
  globalSearchCompanies,
  isLoading: isLoadingCompanies,
  hasError: hasCompanyError,
  updateGlobalCompanySearchQuery,
  getCompanyUpdateById,
} = useGlobalCompanySearch(props.countryCode);

// Convert companies to options format required by SearchableSelectComponent
const companyOptions = computed(() =>
  globalSearchCompanies.value.map((globalSearchCompany) => ({
    label: globalSearchCompany.name || "Unknown Company",
    value: String(globalSearchCompany.id),
    description: join(
      filter(
        [
          globalSearchCompany.country && !props.countryCode
            ? `${globalSearchCompany.country}`
            : null,
          globalSearchCompany.business_id
            ? `ID: ${globalSearchCompany.business_id}`
            : null,
          globalSearchCompany.vat ? `VAT: ${globalSearchCompany.vat}` : null,
          globalSearchCompany.account_eecs
            ? `EECS: ${globalSearchCompany.account_eecs}`
            : null,
          globalSearchCompany.address,
          globalSearchCompany.devices?.length
            ? `${globalSearchCompany.devices.length} ${
                globalSearchCompany.devices.length === 1 ? "device" : "devices"
              }`
            : null,
        ],
        Boolean
      ),
      " | "
    ),
    // Store full company object as a custom property
    _company: globalSearchCompany,
  }))
);

// Create a custom entry option for user-typed text that doesn't match any company
const customEntryOption = computed(() => {
  let customText = null;
  let description = "";

  if (props.modelValue) {
    // Check if modelValue matches any company option
    const matchingOption = companyOptions.value.find(
      (option) => option.label === props.modelValue
    );
    if (!matchingOption) {
      // modelValue doesn't match any database company, treat as custom entry
      customText = props.modelValue;
      description = "Custom entry";
    }
  }

  if (customText) {
    return {
      label: customText,
      value: "custom_entry",
      description,
      _isCustomEntry: true,
      _company: {
        id: -1,
        name: customText,
        country: "",
        devices: [],
      },
    } as any;
  }

  return null;
});

// Combine custom entry option with search results
const combinedOptions = computed(() => {
  const options = [...companyOptions.value];
  if (customEntryOption.value) {
    options.unshift(customEntryOption.value);
  }
  return options;
});

// Compute the selected value for the SearchableSelectComponent
const selectedValue = computed(() => {
  // If we have a custom entry, use that
  if (customEntryOption.value) {
    return "custom_entry";
  }

  // Try to find a matching company option based on modelValue
  if (props.modelValue) {
    const matchingOption = companyOptions.value.find(
      (option) => option.label === props.modelValue
    );
    if (matchingOption) {
      return matchingOption.value;
    }
  }

  return undefined;
});

const handleSelectCompanyById = (id: string) => {
  // Check if it's the custom entry option
  if (id === "custom_entry") {
    const customText = customEntryOption.value?.label || "";
    emit("update:modelValue", customText);
    return;
  }

  // Try to get company from database
  const updatedCompany = getCompanyUpdateById(id);
  if (updatedCompany) {
    emit("select", updatedCompany);
    emit("update:modelValue", updatedCompany.name || "");
    // Reset the search field to empty after selection
    updateGlobalCompanySearchQuery("");
  } else {
    // If no company found, treat it as arbitrary text input
    emit("update:modelValue", id);
  }
};

const handleQueryUpdated = (query: string) => {
  updateGlobalCompanySearchQuery(query);
  emit("input", query);
  emit("update:modelValue", query);
};
</script>
